export type BlogPublishOutputId =
  | "article_page"
  | "blog_discovery"
  | "subscriber_newsletter"
  | "social_draft";

export type BlogPublishOutputStatus = "ready" | "complete" | "disabled";

export interface BlogPublishOutputPlanItem {
  id: BlogPublishOutputId;
  label: string;
  status: BlogPublishOutputStatus;
  contract: string;
  description: string;
}

export interface BuildBlogPublishOutputPlanInput {
  isPublished?: boolean | null;
  publishedUrl?: string | null;
  sendToSubscribersOnPublish?: boolean | null;
  newsletterSent?: boolean | null;
  createSocialDraftOnPublish?: boolean | null;
  socialDraftPostId?: string | null;
}

export function buildBlogPublishOutputPlan(
  input: BuildBlogPublishOutputPlanInput = {},
): BlogPublishOutputPlanItem[] {
  const articlePublished = input.isPublished === true || Boolean(input.publishedUrl);
  const sendNewsletter = input.sendToSubscribersOnPublish !== false;
  const newsletterSent = input.newsletterSent === true;
  const createSocialDraft = input.createSocialDraftOnPublish !== false;
  const hasSocialDraft = Boolean(input.socialDraftPostId);

  return [
    {
      id: "article_page",
      label: "Published article page",
      status: articlePublished ? "complete" : "ready",
      contract: "blog.publish -> page.status + syncBlogPage",
      description: articlePublished
        ? "The backing page is published and synced to the blog URL."
        : "Publish will mark the backing page live and sync SEO metadata to the public article URL.",
    },
    {
      id: "blog_discovery",
      label: "Blog listings and RSS",
      status: "ready",
      contract: "regenerate_blog_listings queue",
      description: "Publish enqueues the site-publisher job that rebuilds index, taxonomy, author, and RSS artifacts.",
    },
    {
      id: "subscriber_newsletter",
      label: "Subscriber newsletter",
      status: sendNewsletter ? (newsletterSent ? "complete" : "ready") : "disabled",
      contract: "blog.post_published -> blog-newsletter target",
      description: sendNewsletter
        ? "The event-router newsletter target will queue subscriber email notifications after publish."
        : "Disabled for this post; subscribers will not receive the publish email.",
    },
    {
      id: "social_draft",
      label: "Social draft",
      status: createSocialDraft ? (hasSocialDraft ? "complete" : "ready") : "disabled",
      contract: "blog.post_published -> blog-social target",
      description: createSocialDraft
        ? "The event-router social target will create a linked draft for manual review."
        : "Disabled for this post; no social draft will be created.",
    },
  ];
}

export function countEnabledBlogPublishOutputs(
  outputs: BlogPublishOutputPlanItem[],
): number {
  return outputs.filter((output) => output.status !== "disabled").length;
}
